/**
 * InlineEditableField — Workforce Hub W3 single-field inline editor.
 *
 * Goals:
 *   - Replace a center-screen modal for one-field edits with a
 *     click-to-edit affordance that lives in place.
 *   - Optimistic UI with explicit rollback on error.
 *   - Save on Enter / blur, cancel on Escape — never a separate Save
 *     button at the top of the workspace.
 *   - Permission-aware: when readOnly is true, render the display
 *     mode only (no click-to-edit affordance, no keyboard hint).
 *   - Telemetry: emits edit.start / edit.commit / edit.abandon /
 *     edit.fail via the existing workforce telemetry hook so behavior
 *     is comparable to modal-based edits.
 *
 * Out of scope (W3):
 *   - Multi-field edits — those keep using their modals until W4.
 *   - File uploads — those still go through their drawer flows.
 *   - Destructive ops (delete, archive, password reset) — those keep
 *     using <ConfirmDialog>.
 *
 * Public API:
 *   <InlineEditableField
 *     value={profile.preferredName}
 *     type="text" | "email" | "tel" | "date"
 *     label="Preferred name"          // for screen readers + error context
 *     readOnly={!perms.canWritePII}
 *     placeholder="—"
 *     emptyDisplay="—"                // shown in read mode when value is empty
 *     onSave={async (next) => { ... }}// MUST throw on validation/server error
 *     formatDisplay={(value) => ...}  // optional display formatter
 *     telemetrySubtype="inline_preferred_name"
 *     data-testid="inline-edit-preferred-name"
 *   />
 *
 * Errors:
 *   onSave is expected to throw / reject when the value is invalid.
 *   The field catches, rolls the displayed value back to the prior
 *   committed value, shows the error inline, and emits edit.fail.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import useWorkforceTelemetry from '../../hooks/useWorkforceTelemetry';
import './InlineEditableField.css';

const MODE_READ = 'read';
const MODE_EDITING = 'editing';
const MODE_SAVING = 'saving';

function defaultFormatDisplay(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

export default function InlineEditableField({
  value,
  type = 'text',
  label,
  readOnly = false,
  placeholder = '',
  emptyDisplay = '—',
  onSave,
  formatDisplay = defaultFormatDisplay,
  telemetrySubtype = null,
  'data-testid': testId,
  className = ''
}) {
  const [committedValue, setCommittedValue] = useState(value);
  const [draftValue, setDraftValue] = useState(value ?? '');
  const [mode, setMode] = useState(MODE_READ);
  const [errorMessage, setErrorMessage] = useState('');
  const { recordEvent } = useWorkforceTelemetry();
  const inputRef = useRef(null);
  // Used to skip the blur-save when the user cancels via Esc — the
  // input loses focus right after the keydown handler clears mode.
  const cancelledRef = useRef(false);

  // Mirror prop changes when the field is in read mode (server bumped
  // the value via a refetch). The effect only fires when the prop
  // itself changes — not when `mode` toggles — so the component stays
  // authoritative on its own committedValue immediately after a
  // successful save (parent refetch may lag the optimistic commit).
  // An in-progress edit (mode=editing|saving) preserves the draft.
  const lastSeenPropRef = useRef(value);
  useEffect(() => {
    if (lastSeenPropRef.current === value) return;
    lastSeenPropRef.current = value;
    if (mode === MODE_READ) {
      setCommittedValue(value);
      setDraftValue(value ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Focus + select on entering edit mode.
  useEffect(() => {
    if (mode === MODE_EDITING && inputRef.current) {
      inputRef.current.focus();
      // For text-ish inputs, select-all so a single keystroke
      // replaces the value.
      if (typeof inputRef.current.select === 'function' && type !== 'date') {
        inputRef.current.select();
      }
    }
  }, [mode, type]);

  const emitTelemetry = useCallback((eventType, extraProps = {}) => {
    if (!telemetrySubtype) return;
    recordEvent({
      eventType,
      eventSubtype: telemetrySubtype,
      // We intentionally do NOT log values — PII safety contract.
      properties: extraProps
    });
  }, [recordEvent, telemetrySubtype]);

  const enterEdit = useCallback(() => {
    if (readOnly) return;
    cancelledRef.current = false;
    setDraftValue(committedValue ?? '');
    setErrorMessage('');
    setMode(MODE_EDITING);
    emitTelemetry('edit.start');
  }, [readOnly, committedValue, emitTelemetry]);

  const cancelEdit = useCallback((reason = 'cancel') => {
    cancelledRef.current = true;
    setDraftValue(committedValue ?? '');
    setErrorMessage('');
    setMode(MODE_READ);
    emitTelemetry('edit.abandon', { reason });
  }, [committedValue, emitTelemetry]);

  const commit = useCallback(async () => {
    // Treat a no-op (same as committed value) as a silent close — no
    // network call, no telemetry beyond the implicit abandon.
    const normalizedDraft = draftValue === null || draftValue === undefined ? '' : String(draftValue);
    const normalizedCommitted = committedValue === null || committedValue === undefined ? '' : String(committedValue);
    if (normalizedDraft === normalizedCommitted) {
      setMode(MODE_READ);
      return;
    }

    if (!onSave) {
      // No save handler — treat like a cancel.
      setMode(MODE_READ);
      return;
    }

    setMode(MODE_SAVING);
    setErrorMessage('');
    const started = Date.now();
    try {
      // Pass empty string as null so callers can clear a field cleanly.
      const next = normalizedDraft === '' ? null : normalizedDraft;
      await onSave(next);
      setCommittedValue(next);
      setDraftValue(next ?? '');
      setMode(MODE_READ);
      emitTelemetry('edit.commit', { latencyMs: Date.now() - started });
    } catch (err) {
      // Roll back to the prior committed value on failure. The error
      // is shown inline; the input stays in edit mode so the operator
      // can correct + retry without losing context.
      setDraftValue(committedValue ?? '');
      setMode(MODE_EDITING);
      setErrorMessage(err?.message || 'Save failed.');
      emitTelemetry('edit.fail', {});
    }
  }, [draftValue, committedValue, onSave, emitTelemetry]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      cancelledRef.current = false;
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit('esc');
    }
  };

  const handleBlur = () => {
    if (cancelledRef.current) return;
    // Defer until microtask so click-on-Cancel-button doesn't race
    // the blur handler.
    setTimeout(() => {
      if (cancelledRef.current) return;
      if (mode === MODE_EDITING) {
        commit();
      }
    }, 0);
  };

  // ── Render ─────────────────────────────────────────────────────

  const displayedValue = mode === MODE_SAVING
    // While saving, optimistically show the draft so the UI feels
    // instant. If commit() rolls back, the displayed value reverts.
    ? formatDisplay(draftValue === '' ? null : draftValue)
    : formatDisplay(committedValue);
  const isEmpty = displayedValue === '' || displayedValue === null || displayedValue === undefined;

  if (readOnly) {
    return (
      <span
        className={`inline-edit inline-edit--readonly${className ? ` ${className}` : ''}`}
        data-testid={testId}
        data-mode="readonly"
      >
        {isEmpty ? emptyDisplay : displayedValue}
      </span>
    );
  }

  if (mode === MODE_READ) {
    return (
      <button
        type="button"
        className={`inline-edit inline-edit--read${className ? ` ${className}` : ''}${isEmpty ? ' inline-edit--empty' : ''}`}
        data-testid={testId}
        data-mode="read"
        aria-label={label ? `${label}. Click to edit.` : 'Click to edit.'}
        onClick={enterEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            enterEdit();
          }
        }}
      >
        {isEmpty ? emptyDisplay : displayedValue}
      </button>
    );
  }

  return (
    <span
      className={`inline-edit inline-edit--editing${className ? ` ${className}` : ''}`}
      data-testid={testId}
      data-mode={mode}
    >
      <input
        ref={inputRef}
        type={type}
        value={draftValue ?? ''}
        placeholder={placeholder}
        aria-label={label || undefined}
        disabled={mode === MODE_SAVING}
        onChange={(e) => setDraftValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {mode === MODE_SAVING ? (
        <span className="inline-edit__status" data-testid={testId ? `${testId}-saving` : undefined} aria-live="polite">
          Saving…
        </span>
      ) : null}
      {errorMessage ? (
        <span
          className="inline-edit__error"
          role="alert"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {errorMessage}
        </span>
      ) : null}
    </span>
  );
}
